import React, { useMemo } from 'react';
import { Search, Bot, Shield, Users, ChevronRight, Zap, Clock, TrendingUp, ArrowRight, CheckCircle2, AlertCircle, Loader2, GitFork, Activity } from 'lucide-react';
import { AGENTS, DEPT_GROUPS, TEAM_MEMBERS, ORG_SUMMARY, getDeptGroupsForRole, getAgentsForRole, getActivityForRole } from '../../data/agentsData';
import { AGENT_THUMBS } from './V4BriefingShared';

const DEPT_COLORS = {
  shared: { badge: 'bg-slate-500/10 text-slate-500' },
  strategy: { badge: 'bg-indigo-500/10 text-indigo-400' },
  marketing: { badge: 'bg-rose-500/10 text-rose-400' },
  sales: { badge: 'bg-amber-500/10 text-amber-400' },
  support: { badge: 'bg-emerald-500/10 text-emerald-400' },
  people: { badge: 'bg-fuchsia-500/10 text-fuchsia-400' },
  finance: { badge: 'bg-green-500/10 text-green-400' },
};

const AgentCard = ({ agent, isDark, onClick, teamCount }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const hoverBg = isDark ? 'hover:bg-[#0f2035]' : 'hover:bg-[#ece5db]';

  return (
    <div
      className={`${cardBg} border ${border} rounded-xl p-3.5 ${hoverBg} transition-colors cursor-pointer group`}
      onClick={() => onClick(agent.id)}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-start gap-2.5 mb-2.5">
        <img src={AGENT_THUMBS[agent.id]} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={`text-[12px] font-semibold ${tp} truncate`}>{agent.name}</p>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${agent.status === 'active' ? 'bg-emerald-400' : 'bg-white/20'}`} />
          </div>
          <p className={`text-[10px] ${ts} mt-0.5`}>{agent.role}</p>
        </div>
        <ChevronRight className={`w-3.5 h-3.5 ${ts} opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1`} />
      </div>

      <p className={`text-[10px] leading-relaxed ${ts} mb-2.5 line-clamp-2`}>{agent.coreResponsibility}</p>

      <div className={`flex items-center gap-3 pt-2.5 border-t ${border}`}>
        <span className={`flex items-center gap-1 text-[9px] ${ts}`}>
          <Zap className="w-2.5 h-2.5" /><span className={`font-semibold ${tp}`}>{agent.metrics.tasksCompleted.toLocaleString()}</span> tasks
        </span>
        <span className={`flex items-center gap-1 text-[9px] ${ts}`}>
          <Clock className="w-2.5 h-2.5" /><span className={`font-semibold ${tp}`}>{agent.metrics.hoursSaved}h</span>/mo
        </span>
        {teamCount > 0 && (
          <span className={`flex items-center gap-1 text-[9px] ${ts} ml-auto`}>
            <Users className="w-2.5 h-2.5" />{teamCount}
          </span>
        )}
      </div>
    </div>
  );
};

const DeptGroupSection = ({ group, isDark, onAgentClick, isCeo }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const deptColor = DEPT_COLORS[group.id] || DEPT_COLORS.shared;
  const agents = group.agents.map(id => AGENTS.find(a => a.id === id)).filter(Boolean);

  const getTeamCount = (agentId) => {
    return Object.values(TEAM_MEMBERS).flat().filter(m => m.agents.includes(agentId)).length;
  };

  return (
    <div className="mb-4" data-testid={`dept-group-${group.id}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${ts}`}>{group.label}</p>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${deptColor.badge}`}>{agents.length}</span>
        {group.admin && (
          <span className={`flex items-center gap-1 text-[9px] ${ts} ml-auto`}>
            <Shield className="w-2.5 h-2.5" />{group.adminName}
          </span>
        )}
        {!group.admin && (
          <span className={`text-[9px] ${ts} ml-auto`}>Everyone</span>
        )}
      </div>
      <div className={`grid ${isCeo ? 'grid-cols-2' : 'grid-cols-2'} gap-2.5`}>
        {agents.map(agent => (
          <AgentCard key={agent.id} agent={agent} isDark={isDark} onClick={onAgentClick} teamCount={getTeamCount(agent.id)} />
        ))}
      </div>
    </div>
  );
};

const ImpactBanner = ({ isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  const stats = [
    { label: 'Team', value: `${ORG_SUMMARY.totalHumans} humans`, sub: `+ ${ORG_SUMMARY.totalAgents} agents` },
    { label: 'Replaces', value: `${ORG_SUMMARY.traditionalHeadcount} FTEs`, sub: ORG_SUMMARY.annualSavings + '/yr saved' },
    { label: 'Hours Saved', value: ORG_SUMMARY.totalHoursSaved, sub: 'all depts' },
    { label: 'Automated', value: ORG_SUMMARY.totalTasksAutomated, sub: 'this quarter' },
  ];

  return (
    <div className={`${cardBg} border ${border} rounded-xl p-3.5 mb-4`} data-testid="impact-banner">
      <div className="flex items-center gap-2 mb-2.5">
        <TrendingUp className={`w-3.5 h-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${ts}`}>Organization Impact</p>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.label}>
            <p className={`text-[9px] ${ts} mb-0.5`}>{s.label}</p>
            <p className={`text-[13px] font-bold ${tp}`}>{s.value}</p>
            <p className={`text-[9px] ${isDark ? 'text-emerald-400/70' : 'text-emerald-600/70'}`}>{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Activity Feed Components ────────────────────
const TYPE_CONFIG = {
  completed: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Completed' },
  'in-progress': { icon: Loader2, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'In Progress' },
  handoff: { icon: ArrowRight, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Handoff' },
  approval: { icon: AlertCircle, color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Approval Needed' },
  parallel: { icon: GitFork, color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Parallel Run' },
  started: { icon: Zap, color: 'text-indigo-400', bg: 'bg-indigo-500/10', label: 'Started' },
};

const ActivityItem = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-black/[0.02]';
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.completed;
  const Icon = config.icon;
  const agent = AGENTS.find(a => a.id === item.agent);
  const targetAgent = item.targetAgent ? AGENTS.find(a => a.id === item.targetAgent) : null;

  return (
    <div className={`px-3.5 py-2.5 ${hoverBg} transition-colors border-b ${border} last:border-b-0`} data-testid={`activity-item-${item.id}`}>
      <div className="flex items-start gap-2.5">
        <div className="relative flex-shrink-0 mt-0.5">
          <img src={AGENT_THUMBS[item.agent]} alt="" className="w-6 h-6 rounded-full object-cover" />
          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${config.bg} flex items-center justify-center`}>
            <Icon className={`w-1.5 h-1.5 ${config.color}`} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-[10px] font-semibold ${tp}`}>{agent?.name}</span>
            {item.type === 'handoff' && targetAgent && (
              <>
                <ArrowRight className={`w-2.5 h-2.5 ${config.color}`} />
                <span className={`text-[10px] font-semibold ${tp}`}>{targetAgent.name}</span>
              </>
            )}
            {item.type === 'parallel' && item.parallelAgents && (
              <span className={`text-[9px] ${config.color}`}>+ {item.parallelAgents.length} agents</span>
            )}
          </div>
          <p className={`text-[10px] leading-relaxed ${ts}`}>{item.description}</p>
          {item.type === 'approval' && item.assignedTo && (
            <span className={`inline-flex items-center gap-1 mt-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
              <AlertCircle className="w-2.5 h-2.5" /> Waiting: {item.assignedTo}
            </span>
          )}
          {item.type === 'parallel' && item.parallelAgents && (
            <div className="flex items-center gap-1 mt-1">
              {item.parallelAgents.map(id => (
                <img key={id} src={AGENT_THUMBS[id]} alt="" className="w-4 h-4 rounded-full object-cover border border-white/10" />
              ))}
            </div>
          )}
        </div>
        <span className={`text-[9px] ${ts} flex-shrink-0 mt-0.5`}>{item.time}</span>
      </div>
    </div>
  );
};

const ActivityFeed = ({ isDark, activeUser }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const activity = useMemo(() => getActivityForRole(activeUser.id), [activeUser.id]);

  const completed = activity.filter(a => a.type === 'completed').length;
  const pending = activity.filter(a => a.type === 'approval').length;
  const handoffs = activity.filter(a => a.type === 'handoff').length;

  return (
    <div className="flex flex-col h-full" data-testid="activity-feed">
      {/* Feed header */}
      <div className="px-3.5 pt-3.5 pb-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 mb-2.5">
          <Activity className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
          <p className={`text-[11px] font-semibold ${tp}`}>Live Activity</p>
          <span className="relative flex h-2 w-2 ml-auto">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
          </span>
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-2">
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
            {completed} done
          </span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
            {handoffs} handoffs
          </span>
          {pending > 0 && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-orange-500/10 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
              {pending} pending
            </span>
          )}
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* Feed items */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {activity.map(item => (
          <ActivityItem key={item.id} item={item} isDark={isDark} />
        ))}
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────
const V4AgentsList = ({ isDark, activeUser, onViewAgent }) => {
  const [search, setSearch] = React.useState('');
  const isCeo = activeUser.id === 'ceo';
  const groups = useMemo(() => getDeptGroupsForRole(activeUser.id), [activeUser.id]);
  const allAgents = useMemo(() => getAgentsForRole(activeUser.id), [activeUser.id]);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const inputBg = isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]';

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups.map(g => ({
      ...g,
      agents: g.agents.filter(id => {
        const agent = AGENTS.find(a => a.id === id);
        return agent && (agent.name.toLowerCase().includes(q) || agent.role.toLowerCase().includes(q) || agent.coreResponsibility.toLowerCase().includes(q));
      }),
    })).filter(g => g.agents.length > 0);
  }, [groups, search]);

  const pageTitle = isCeo ? 'Agents' : `${groups[0]?.label || ''} Agents`;
  const adminBadge = !isCeo ? activeUser.name : null;

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="agents-list-page">
      {/* Header — full width */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Bot className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>{pageTitle}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/40' : 'bg-black/[0.04] text-slate-500'}`}>
              {allAgents.length} agents
            </span>
          </div>
          {adminBadge && (
            <span className={`flex items-center gap-1.5 text-[10px] ${ts}`}>
              <Shield className={`w-3 h-3 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              Admin: <span className={`font-semibold ${tp}`}>{adminBadge}</span>
            </span>
          )}
        </div>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${inputBg} border ${border}`}>
          <Search className={`w-3.5 h-3.5 ${ts}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search agents by name, role, or capability..."
            className={`flex-1 bg-transparent outline-none text-xs ${tp} placeholder:${ts}`}
            data-testid="agents-search"
          />
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* Two-column layout: Directory (left) + Activity Feed (right) */}
      <div className="flex-1 min-h-0 flex">
        {/* Left: Agent Directory */}
        <div className={`flex-1 min-w-0 overflow-y-auto px-5 py-4 border-r ${border}`}>
          {isCeo && <ImpactBanner isDark={isDark} />}

          {filteredGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Bot className={`w-8 h-8 mb-3 ${ts}`} />
              <p className={`text-sm font-medium ${tp} mb-1`}>No agents found</p>
              <p className={`text-xs ${ts}`}>Try adjusting your search query</p>
            </div>
          ) : (
            filteredGroups.map(group => (
              <DeptGroupSection key={group.id} group={group} isDark={isDark} onAgentClick={onViewAgent} isCeo={isCeo} />
            ))
          )}
        </div>

        {/* Right: Persistent Activity Feed */}
        <div className="w-[320px] flex-shrink-0">
          <ActivityFeed isDark={isDark} activeUser={activeUser} />
        </div>
      </div>
    </div>
  );
};

export default V4AgentsList;
